import type { DataProvider } from '@refinedev/core';
import * as Sentry from '@sentry/react';
import { roundToNearestMinutes } from 'date-fns';
import { useCallback, useEffect, useMemo, useState } from 'react';

/**
 * E57 · THE ONE CLIENT-SESSION TIMER (founder Q-e57g1-2 A, 2026-10-17; registry `rtm-coupling` rule 1).
 *
 * Start Client Session on the client page and the header's Filter-by-Client bar share ONE timer. Its state — the client it
 * was STARTED for and the start (and optional end) time — lives here, in the therapist-activity context App.tsx provides,
 * never in a component's local state: Finish records the session for the PINNED client whichever page is open when it is
 * pressed (start Maya, open Ravi, finish → saved for Maya), and leaving a page never loses it.
 *
 * Finish is the same UpsertSession the bar always sent (E17 — `upsertSession`, `_to` = the client, `createdAt` the start,
 * `finishedAt` the end or now rounded up to the minute). The timer survives a reload of the tab (sessionStorage — the
 * session's lifetime, never across tabs or after the tab closes); a storage failure only loses that convenience.
 */
export type ClientSessionTimer = {
  clientId: string;
  startedAt: string;
  endedAt: string | null;
};

export type ClientSessionTimerApi = {
  timer: ClientSessionTimer | null;
  start: (clientId: string) => void;
  setStartedAt: (at: Date) => void;
  setEndedAt: (at: Date | null) => void;
  /**
   * Records the session for the pinned client; resolves true when the API answered with a row. Rejects with the data
   * provider's normalised error: a coded server refusal (`code` set) has already ended the timer.
   */
  finish: () => Promise<boolean>;
  saving: boolean;
};

/**
 * What Finish's failure says: a coded refusal carries the server's own localised, actionable sentence (normalizeDataError)
 * and is shown as is; anything else (the network, an uncoded failure) gets `fallback` — "press Finish again".
 */
export const sessionFinishFailure = (error: unknown, fallback: string): string => {
  const e = error as { code?: string; message?: string } | null;
  return e?.code && e.message ? e.message : fallback;
};

const STORAGE_KEY = 'cc.clientSessionTimer';

const read = (): ClientSessionTimer | null => {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const v = JSON.parse(raw) as ClientSessionTimer;
    return v && typeof v.clientId === 'string' && typeof v.startedAt === 'string' ? v : null;
  } catch {
    return null;
  }
};

const write = (v: ClientSessionTimer | null): void => {
  try {
    if (v) window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(v));
    else window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // private mode / blocked storage: the timer still works for this page's lifetime
  }
};

export const useClientSessionTimerState = (
  dataProvider: Required<DataProvider>,
): ClientSessionTimerApi => {
  const [timer, setTimer] = useState<ClientSessionTimer | null>(read);
  const [saving, setSaving] = useState(false);

  useEffect(() => write(timer), [timer]);

  const start = useCallback((clientId: string) => {
    setTimer({
      clientId,
      startedAt: roundToNearestMinutes(new Date(), { nearestTo: 1, roundingMethod: 'floor' }).toISOString(),
      endedAt: null,
    });
  }, []);

  const setStartedAt = useCallback(
    (at: Date) => setTimer((t) => (t ? { ...t, startedAt: at.toISOString() } : t)),
    [],
  );
  const setEndedAt = useCallback(
    (at: Date | null) => setTimer((t) => (t ? { ...t, endedAt: at ? at.toISOString() : null } : t)),
    [],
  );

  const finish = useCallback(async (): Promise<boolean> => {
    const t = timer;
    if (!t) return false;
    setSaving(true);
    try {
      const res = await dataProvider.custom({
        url: '',
        method: 'post',
        meta: {
          operation: 'upsertSession',
          fields: ['_id'],
          variables: {
            input: {
              name: 'input',
              type: 'SessionInput',
              value: {
                data: {
                  createdAt: new Date(t.startedAt),
                  finishedAt: t.endedAt
                    ? new Date(t.endedAt)
                    : roundToNearestMinutes(new Date(), { nearestTo: 1, roundingMethod: 'ceil' }),
                },
                // the PINNED client — never whichever client page is open now
                where: { _to: t.clientId },
              },
            },
          },
        },
      });
      setTimer(null);
      return !!res?.data;
    } catch (error) {
      // a CODED refusal is the server's answer (the E08 Create gate: nothing is recorded, pressing Finish again cannot
      // change it) — the timer ends as it always did and the caller shows the server's own sentence; a network or
      // uncoded failure keeps the timer so Finish can be pressed again
      if ((error as { code?: string } | null)?.code) setTimer(null);
      else Sentry.captureException(error);
      throw error;
    } finally {
      setSaving(false);
    }
  }, [timer, dataProvider]);

  return useMemo(
    () => ({ timer, start, setStartedAt, setEndedAt, finish, saving }),
    [timer, start, setStartedAt, setEndedAt, finish, saving],
  );
};
